import { useEffect, useRef, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { EmptyState, ErrorMessage, Spinner } from '../components/common';
import { ResultNotice } from '../components/ResultNotice';
import { formatDate, formatMoney, formatShortDate } from '../lib/format';
import { useApi, useDocumentTitle } from '../hooks/useApi';
import { usePaymentReturn } from '../hooks/usePaymentReturn';
import { openAuthenticatedFile } from '../lib/download';
import { api, ApiError, INVOICES_BEFORE_CHECKOUT } from '../lib/api';
import { useAuth } from '../lib/useAuth';
import './account.css';

type Invoice = {
  number: string;
  issueDate: string;
  total: number;
  tax: number;
  currency: string;
  isRectification: boolean;
  hasPdf: boolean;
  /** 'available', 'review', 'refunded' o null si ya no se puede pedir. */
  refund: string | null;
  refundDeadline: string | null;
};

type RefundResult = { outcome: string; message: string };

function readInvoicesBeforeCheckout(): string[] | null {
  try {
    const raw = sessionStorage.getItem(INVOICES_BEFORE_CHECKOUT);
    sessionStorage.removeItem(INVOICES_BEFORE_CHECKOUT);
    return raw ? (JSON.parse(raw) as string[]) : null;
  } catch {
    return null;
  }
}

/** Lo que se espera ver llegar a la lista: la factura de un pago o la rectificativa de un reembolso. */
type Watch = { rectification: boolean; known: string[] };

// Una rectificativa devuelve dinero: se enseña en negativo, como en el propio documento.
const sign = (invoice: Invoice) => (invoice.isRectification ? -1 : 1);

const WITHDRAWAL_DAYS = 14;

// El plazo llega como fecha sin hora: se compara con el día de hoy, no con este instante.
function daysLeft(deadline: string): number {
  const now = new Date();
  const today = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate());
  return Math.max(0, Math.round((Date.parse(deadline) - today) / 86_400_000));
}

function daysLeftLabel(days: number): string {
  if (days === 0) {
    return 'Hoy es el último día';
  }

  return days === 1 ? 'Queda 1 día' : `Quedan ${days} días`;
}

export function InvoicesPage() {
  useDocumentTitle('Mis facturas');

  const { user } = useAuth();
  const { data, error, loading, reload } = useApi<Invoice[]>('/me/invoices');
  const justPaid = usePaymentReturn();
  const [noticeOpen, setNoticeOpen] = useState(justPaid);
  const [paymentWatched, setPaymentWatched] = useState(false);
  const [watch, setWatch] = useState<Watch | null>(null);
  const [attempts, setAttempts] = useState(0);
  const dialog = useRef<HTMLDialogElement>(null);
  const [selected, setSelected] = useState<Invoice | null>(null);
  const [reason, setReason] = useState('');
  const [sending, setSending] = useState(false);
  const [requestError, setRequestError] = useState<string | null>(null);
  const [result, setResult] = useState<RefundResult | null>(null);

  // Las facturas que había antes de ir a pagar (las apunta quien abre el pago). Si no están
  // apuntadas, valen las de la primera carga: el aviso de Stripe suele llegar después.
  if (justPaid && data && !paymentWatched) {
    setPaymentWatched(true);
    setWatch({ rectification: false, known: readInvoicesBeforeCheckout() ?? data.map((invoice) => invoice.number) });
  }

  const arrived =
    watch && data
      ? (data.find(
          (invoice) => invoice.isRectification === watch.rectification && !watch.known.includes(invoice.number),
        ) ?? null)
      : null;
  const waiting = watch !== null && arrived === null && attempts < 10;

  // Stripe avisa a la API unos segundos después de que el alumno vuelva: se mira cada 3 s, hasta 30.
  useEffect(() => {
    if (!waiting) {
      return;
    }

    const timer = window.setTimeout(() => {
      setAttempts((value) => value + 1);
      reload();
    }, 3000);
    return () => window.clearTimeout(timer);
  }, [waiting, attempts, reload]);

  function download(invoice: Invoice) {
    /*
      No es un enlace: el endpoint exige sesión y una navegación normal llega sin la cabecera
      del token, así que devolvía 401 en vez del PDF.
    */
    void openAuthenticatedFile(`/me/invoices/${invoice.number}/pdf`, `${invoice.number}.pdf`).catch(() => undefined);
  }

  function openRequest(invoice: Invoice) {
    setSelected(invoice);
    setReason('');
    setRequestError(null);
    dialog.current?.showModal();
  }

  async function confirm(event: FormEvent) {
    event.preventDefault();

    if (!selected) {
      return;
    }

    setSending(true);
    setRequestError(null);

    try {
      const answer = await api.post<RefundResult>(`/me/invoices/${selected.number}/refund`, { reason });
      setResult(answer);
      setNoticeOpen(false);
      dialog.current?.close();

      // La rectificativa la emite el aviso del reembolso, que llega detrás de esta respuesta.
      if (answer.outcome === 'refunded') {
        setWatch({ rectification: true, known: data?.map((invoice) => invoice.number) ?? [] });
        setAttempts(0);
      }

      reload();
    } catch (caught) {
      setRequestError(caught instanceof ApiError ? caught.message : 'No hemos podido enviar tu solicitud.');
    } finally {
      setSending(false);
    }
  }

  return (
    <div className="container section account">
      <header className="section-header">
        <h1>Facturas</h1>
        <p className="muted">
          <Link to="/cuenta">← Volver a mi cuenta</Link>
        </p>
      </header>

      {noticeOpen && !result && (
        <ResultNotice
          title="Pago completado"
          celebrate
          onClose={() => setNoticeOpen(false)}
          actions={
            arrived?.hasPdf && (
              <button type="button" className="btn btn--primary btn--sm" onClick={() => download(arrived)}>
                Descargar factura
              </button>
            )
          }
        >
          <p>
            Ya tienes acceso a lo que has comprado.{' '}
            {arrived ? (
              <>
                Tu factura <strong>{arrived.number}</strong> está lista y te la hemos enviado por email.
              </>
            ) : (
              'Tu factura aparecerá en esta lista en unos segundos y también te llegará por email.'
            )}
          </p>
        </ResultNotice>
      )}

      {result && (
        <ResultNotice
          title={result.outcome === 'refunded' ? 'Reembolso completado' : 'Solicitud recibida'}
          tone={result.outcome === 'refunded' ? 'success' : 'pending'}
          onClose={() => setResult(null)}
          actions={
            arrived?.hasPdf && (
              <button type="button" className="btn btn--primary btn--sm" onClick={() => download(arrived)}>
                Descargar rectificativa
              </button>
            )
          }
        >
          <p>{result.message}</p>
          <p>
            {arrived && (
              <>
                La rectificativa <strong>{arrived.number}</strong> ya está en tu lista.{' '}
              </>
            )}
            Te hemos enviado el acuse de recibo a <strong>{user?.email}</strong>.
          </p>
        </ResultNotice>
      )}

      {loading && <Spinner />}
      {error && <ErrorMessage>No hemos podido cargar tus facturas.</ErrorMessage>}

      {!loading && (data?.length ?? 0) === 0 && (
        <EmptyState title="Todavía no hay facturas">
          <p className="muted">Se emiten automáticamente con cada pago.</p>
        </EmptyState>
      )}

      {data && data.length > 0 && (
        <>
          <div className="table-scroll">
            <table>
              <thead>
                <tr>
                  <th>Número</th>
                  <th>Fecha</th>
                  <th>Base</th>
                  <th>IVA</th>
                  <th>Total</th>
                  <th>Documento</th>
                </tr>
              </thead>
              <tbody>
                {data.map((invoice) => (
                  <tr key={invoice.number} className={invoice === arrived ? 'invoices__row--new' : undefined}>
                    <td>
                      <code>{invoice.number}</code>
                      {invoice.isRectification && (
                        <span className="badge badge--soon" style={{ marginLeft: 'var(--space-2)' }}>
                          Rectificativa
                        </span>
                      )}
                    </td>
                    <td className="invoices__date">{formatShortDate(invoice.issueDate)}</td>
                    <td className="muted">
                      {formatMoney(sign(invoice) * (invoice.total - invoice.tax), invoice.currency)}
                    </td>
                    <td className="muted">{formatMoney(sign(invoice) * invoice.tax, invoice.currency)}</td>
                    <td>
                      <strong>{formatMoney(sign(invoice) * invoice.total, invoice.currency)}</strong>
                    </td>
                    <td>
                      <div className="row">
                        {invoice.hasPdf ? (
                          <button type="button" className="btn btn--ghost btn--sm" onClick={() => download(invoice)}>
                            Descargar
                          </button>
                        ) : (
                          <span className="muted">—</span>
                        )}
                        {invoice.refund === 'available' && (
                          <button type="button" className="btn btn--ghost btn--sm" onClick={() => openRequest(invoice)}>
                            Solicitar reembolso
                          </button>
                        )}
                        {invoice.refund === 'review' && <span className="badge badge--soon">Reembolso en revisión</span>}
                        {invoice.refund === 'refunded' && <span className="badge badge--new">Reembolsada</span>}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <p className="invoices__note">
            Tienes {WITHDRAWAL_DAYS} días desde cada compra para solicitar el reembolso (desistir del contrato).
            Los detalles están en el apartado 5 de los <Link to="/legal/terminos">términos y condiciones</Link>.
          </p>
        </>
      )}

      <dialog ref={dialog} className="refund-dialog" aria-labelledby="refund-title">
        {selected && (
          <form onSubmit={(event) => void confirm(event)}>
            <header>
              <h2 id="refund-title">Solicitar reembolso</h2>
              <p className="refund-dialog__subtitle">Desistir del contrato de esta compra</p>
            </header>

            <div className="refund-dialog__invoice">
              <div className="refund-dialog__invoice-head">
                <code>{selected.number}</code>
                <strong>{formatMoney(selected.total, selected.currency)}</strong>
              </div>
              <p className="refund-dialog__date">Emitida el {formatDate(selected.issueDate)}</p>
              {selected.refundDeadline && (
                <div className="refund-dialog__window">
                  <p>
                    <span>{daysLeftLabel(daysLeft(selected.refundDeadline))}</span>
                    <span className="muted">hasta el {formatDate(selected.refundDeadline)}</span>
                  </p>
                  <div className="refund-dialog__bar">
                    <span
                      style={{
                        width: `${(Math.min(daysLeft(selected.refundDeadline) + 1, WITHDRAWAL_DAYS) / WITHDRAWAL_DAYS) * 100}%`,
                      }}
                    />
                  </div>
                </div>
              )}
            </div>

            <dl className="refund-dialog__terms">
              <dt>Sin empezar</dt>
              <dd>Te devolvemos el importe completo al momento.</dd>
              <dt>Ya empezado</dt>
              <dd>Lo revisamos y te devolvemos la parte que no hayas usado.</dd>
              <dt>Suscripción</dt>
              <dd>Se cancela al confirmar.</dd>
            </dl>

            <div className="field">
              <label htmlFor="refund-reason">
                Motivo <span className="muted">(opcional)</span>
              </label>
              <textarea id="refund-reason" rows={2} value={reason} onChange={(event) => setReason(event.target.value)} />
            </div>

            <p className="refund-dialog__receipt">
              La solicitud va a nombre de <strong>{user?.displayName}</strong>, como tu factura, y el acuse de
              recibo llegará a <strong>{user?.email}</strong>.
            </p>

            {requestError && <ErrorMessage>{requestError}</ErrorMessage>}

            <footer className="refund-dialog__actions">
              <button type="button" className="btn btn--ghost" onClick={() => dialog.current?.close()} disabled={sending}>
                Cancelar
              </button>
              <button type="submit" className="btn btn--primary" disabled={sending}>
                {sending ? 'Enviando…' : 'Confirmar desistimiento'}
              </button>
            </footer>
          </form>
        )}
      </dialog>
    </div>
  );
}
